import type { GameContent } from "@bastion/data";
import { InMemoryConfigRepository, type GameConfigRepository, type ResolvedMap } from "../repos";
import { EventBus, type EventListener } from "../events/EventBus";
import { GameClock } from "../clock/GameClock";
import { SeededRandom } from "../clock/SeededRandom";
import {
  RUNTIME_SNAPSHOT_SCHEMA_VERSION,
  cloneGameState,
  isRuntimeSnapshot,
  type RuntimeSessionStats,
  type RuntimeSnapshot,
} from "../runtimeSnapshot";
import {
  REPLAY_SCHEMA_VERSION,
  type ReplayAdvanceEntry,
  type ReplayCommandEntry,
  type ReplayEntry,
  type ReplayOutcome,
  type ReplayRecordingHeader,
  type ReplayFile,
} from "../replay";
import {
  runCombatTick,
  applySkillDamage,
  tickStatusEffects,
  tickAttackSpeedBoosts,
  type CombatSystemDeps,
} from "../combat/system";
import { WaveSystem, scaleWaveGroups, countWaveEnemies, type WaveSystemDeps } from "../wave/system";
import { generateEndlessWave } from "../wave/endlessGenerator";
import { moveEnemy, replanEnemyPath, BaseDistanceFieldCache } from "../wave/movement";
import { snapToGridCentre, occupiedGridCells, pixelToGrid } from "../grid";
import type {
  EnemyState,
  TowerState,
  GameCommand,
  GameEvent,
  GameState,
  Point,
  Random,
  GameMode,
  PendingOrbitalStrike,
  QueuedGlobalSkill,
  ActiveSkillField,
} from "../types";
import { calculateMonsterGoldReward } from "../combat/rewards";
import type {
  BalanceConfig,
  CombatTuning,
  DifficultyKey,
  EnemyConfig,
  TowerConfig,
  WaveConfig,
} from "@bastion/data";
import { normalizeDifficulty } from "@bastion/data";
export interface EngineOptions {
  seed?: number;
  /** 固定模拟步长（秒）；默认 1/30 */
  stepSeconds?: number;
  /** 覆盖随机数生成器；默认为 `SeededRandom(seed)` */
  random?: Random;
  /** 覆盖时钟；默认为新的 `GameClock` */
  clock?: GameClock;
  /** 难度键；驱动游戏开始时应用哪个 BalanceConfig */
  difficulty?: DifficultyKey;
  /** 调试页允许放置静止怪物并使用无限金币。 */
  mode?: GameMode;
}

export class CommandRejectedError extends Error {
  readonly command: GameCommand;

  constructor(command: GameCommand, reason: string) {
    super(`Command rejected (${command.type}): ${reason}`);
    this.name = "CommandRejectedError";
    this.command = command;
  }
}

const ORBITAL_STRIKE_FLIGHT_SECONDS = 0.72;

/**
 * 单一确定性游戏引擎。
 *
 *   dispatch(command)      → 修改状态 + 发出事件
 *   advanceTo(simSeconds)  → 固定步长 tick，应用波次/战斗/移动逻辑
 *
 * 无 Phaser、无 DOM、无浏览器——同一个类同时驱动无头模拟包和浏览器渲染器。
 */
export class GameEngine {
  readonly state: GameState;
  private readonly repository: GameConfigRepository;
  private readonly content: GameContent;
  private readonly clock: GameClock;
  private random: Random;
  private readonly eventBus: EventBus;
  private readonly stepSeconds: number;
  private readonly difficulty: DifficultyKey;
  private readonly mode: GameMode;
  private readonly balance: BalanceConfig;
  private readonly combatTuning: CombatTuning;
  private initialSeed: number;
  private waveSystem: WaveSystem | null = null;
  private currentWaveIndex = -1;
  private currentWaveConfig: WaveConfig | null = null;
  private mapId: string | null = null;
  /** Debug 模式中放置的敌人配置，仅作用于当前运行会话。 */
  private readonly debugEnemyConfigs: Record<string, EnemyConfig> = {};
  private readonly baseDistanceFieldCache = new BaseDistanceFieldCache();
  /** 整个会话中累计建造的塔数（用于成就统计）。 */
  private towersBuiltSession = 0;
  /** 本会话中到达基地的敌人累计损失的生命值（用于成就统计）。 */
  private livesLostThisGame = 0;
  /** 第 1 波完成时的模拟时间（若尚未完成则为 0）。 */
  private wave1CompleteTime = 0;
  private replayHeader: ReplayRecordingHeader | null = null;
  private replayCommands: ReplayEntry[] = [];
  private replayOrder = 0;
  private replayEnabled = false;
  private replayRecordingStarted = false;
  private replayPlayback = false;
  private replayInternalRestart = false;
  private replayEffectsMuted = false;

  constructor(content: GameContent, options: EngineOptions = {}) {
    this.repository = new InMemoryConfigRepository(content);
    this.content = content;
    this.clock = options.clock ?? new GameClock();
    this.initialSeed = options.seed ?? 1;
    this.random = options.random ?? new SeededRandom(this.initialSeed);
    this.eventBus = new EventBus();
    this.stepSeconds = options.stepSeconds ?? 1 / 30;
    this.difficulty = options.difficulty ?? "hard";
    this.mode = options.mode ?? "normal";
    const difficultyKey = normalizeDifficulty(this.difficulty);
    this.balance = content.difficultyBalances?.[difficultyKey] ?? content.balance;
    this.combatTuning = content.combatTuning ?? {
      id: "combat-compatibility-default",
      splashDamageMultiplier: 0.5,
      chainDamageMultiplier: 0.5,
      towerAutoSkill: { damageMultiplier: 2 },
    };
    this.state = createInitialGameState();
  }

  /** 供渲染器/UI 观察用——请勿直接修改。 */
  get events(): EventBus {
    return this.eventBus;
  }

  get time(): number {
    return this.clock.time();
  }

  get isDebugMode(): boolean {
    return this.mode === "debug";
  }

  /** 启用或关闭本局命令记录；回放引擎必须关闭记录。 */
  setReplayRecording(enabled: boolean): void {
    this.replayEnabled = enabled;
  }

  setReplayEffectsMuted(muted: boolean): void {
    this.replayEffectsMuted = muted;
  }

  setReplayPlayback(enabled: boolean): void {
    this.replayEnabled = false;
    this.replayRecordingStarted = false;
    this.replayCommands = [];
    this.replayHeader = null;
    this.replayPlayback = enabled;
    this.replayInternalRestart = false;
  }

  resetForReplayRestart(): void {
    if (!this.mapId) throw new Error("游戏尚未开始");
    this.random = new SeededRandom(this.initialSeed);
    this.replayInternalRestart = true;
    try {
      this.start(this.mapId);
    } finally {
      this.replayInternalRestart = false;
    }
  }

  getReplayContentFingerprint(): string {
    return this.mapId ? this.contentFingerprint(this.mapId) : "";
  }

  getReplayStepSeconds(): number {
    return this.stepSeconds;
  }

  getReplayMapId(): string | null {
    return this.mapId;
  }

  /** 导出本局已记录的 Replay 文件。 */
  createReplayFile(): ReplayFile {
    if (!this.replayHeader) throw new Error("游戏尚未开始，无法导出战斗回放");
    if (!(this.random instanceof SeededRandom)) {
      throw new Error("当前随机源不支持战斗回放");
    }
    return {
      ...this.replayHeader,
      durationSeconds: this.totalReplayDuration(),
      entries: this.replayCommands.map((entry) =>
        entry.kind === "command" ? { ...entry, command: { ...entry.command } } : { ...entry },
      ),
      outcome: this.replayOutcome(),
    };
  }

  private totalReplayDuration(): number {
    return this.replayCommands.reduce(
      (total, entry) => total + (entry.kind === "advance" ? entry.deltaSeconds : 0),
      0,
    );
  }

  private replayOutcome(): ReplayOutcome | undefined {
    if (this.state.status !== "won" && this.state.status !== "lost") return undefined;
    return {
      status: this.state.status,
      wave: this.state.wave,
      completedAt: this.totalReplayDuration(),
    };
  }

  private createReplayHeader(mapId: string): ReplayRecordingHeader {
    return {
      schemaVersion: REPLAY_SCHEMA_VERSION,
      mapId,
      seed: this.initialSeed,
      difficulty: this.difficulty,
      mode: this.mode,
      stepSeconds: this.stepSeconds,
      contentFingerprint: this.contentFingerprint(mapId),
    };
  }

  private recordReplayCommand(command: GameCommand, accepted: boolean): void {
    if (!this.replayEnabled || !this.replayHeader) return;
    if (command.type === "restart_game" && accepted && this.replayRecordingStarted) {
      this.replayCommands.push({ kind: "restart", order: this.replayOrder++ });
      this.replayInternalRestart = true;
      this.start(this.mapId as string);
      this.replayInternalRestart = false;
      return;
    }
    const entry: ReplayCommandEntry = {
      kind: "command",
      order: this.replayOrder++,
      command: { ...command },
      accepted,
    };
    this.replayCommands.push(entry);
  }

  private recordReplayAdvance(entry: ReplayAdvanceEntry): void {
    if (!this.replayEnabled || !this.replayHeader || !this.replayRecordingStarted) return;
    this.replayCommands.push({ ...entry, order: this.replayOrder++ });
  }

  createRuntimeSnapshot(
    session: RuntimeSessionStats = { enemiesKilled: 0, goldEarned: 0 },
  ): RuntimeSnapshot {
    if (!this.mapId || !this.waveSystem || !(this.random instanceof SeededRandom)) {
      throw new Error("当前运行环境不支持导出完整进度");
    }
    return {
      schemaVersion: RUNTIME_SNAPSHOT_SCHEMA_VERSION,
      mapId: this.mapId,
      difficulty: this.difficulty,
      mode: this.mode,
      contentFingerprint: this.contentFingerprint(this.mapId),
      initialSeed: this.initialSeed,
      clockTime: this.clock.time(),
      randomState: this.random.snapshot(),
      state: cloneGameState(this.state),
      currentWaveIndex: this.currentWaveIndex,
      currentWaveConfig: this.currentWaveConfig
        ? {
            ...this.currentWaveConfig,
            groups: this.currentWaveConfig.groups.map((group) => ({ ...group })),
          }
        : null,
      waveSystem: this.waveSystem.snapshot(),
      session: { ...session },
      towersBuiltSession: this.towersBuiltSession,
      livesLostThisGame: this.livesLostThisGame,
      wave1CompleteTime: this.wave1CompleteTime,
    };
  }

  /**
   * 恢复完整运行态快照。先验证全部兼容条件，再更改引擎；恢复后始终暂停。
   */
  restoreRuntimeSnapshot(snapshot: RuntimeSnapshot): void {
    if (!isRuntimeSnapshot(snapshot)) throw new Error("游戏进度文件格式无效或版本不受支持");
    const resolved = this.repository.getMap(snapshot.mapId);
    if (!resolved) throw new Error(`无法导入：当前版本不存在地图「${snapshot.mapId}」`);
    if (snapshot.mapId !== this.mapId) {
      throw new Error("无法导入：文件地图与当前地图不匹配");
    }
    if (snapshot.difficulty !== this.difficulty || snapshot.mode !== this.mode) {
      throw new Error("无法导入：文件的难度或游戏模式与当前运行配置不匹配");
    }
    if (snapshot.contentFingerprint !== this.contentFingerprint(snapshot.mapId)) {
      throw new Error("无法导入：游戏内容版本与当前版本不匹配");
    }
    if (
      snapshot.currentWaveConfig &&
      !this.isCompatibleWave(snapshot.currentWaveConfig, resolved, false, snapshot.initialSeed)
    ) {
      throw new Error("无法导入：文件中的当前波次与地图配置不匹配");
    }
    if (
      snapshot.waveSystem.currentWaveConfig &&
      !this.isCompatibleWave(
        snapshot.waveSystem.currentWaveConfig,
        resolved,
        true,
        snapshot.initialSeed,
      )
    ) {
      throw new Error("无法导入：文件中的生成波次与地图配置不匹配");
    }
    const nextState = cloneGameState(snapshot.state);
    nextState.status = "paused";
    if (
      snapshot.state.waveInProgress !== (snapshot.currentWaveConfig !== null) ||
      snapshot.state.waveInProgress !== (snapshot.waveSystem.currentWaveConfig !== null)
    ) {
      throw new Error("无法导入：文件中的波次状态不一致");
    }
    const nextRandom = new SeededRandom(snapshot.initialSeed);
    nextRandom.restore(snapshot.randomState);
    const nextWaveSystem = new WaveSystem(this.waveSystemDeps(resolved, nextRandom));
    nextWaveSystem.restore(snapshot.waveSystem);

    this.mapId = snapshot.mapId;
    this.initialSeed = snapshot.initialSeed;
    this.state.status = nextState.status;
    this.state.time = nextState.time;
    this.state.gold = nextState.gold;
    this.state.baseHp = nextState.baseHp;
    this.state.maxHp = nextState.maxHp;
    this.state.wave = nextState.wave;
    this.state.totalWaves = nextState.totalWaves;
    this.state.isEndless = nextState.isEndless;
    this.state.waveInProgress = nextState.waveInProgress;
    this.state.waveCountdown = nextState.waveCountdown;
    this.state.towers = nextState.towers;
    this.state.enemies = nextState.enemies;
    this.state.projectiles = nextState.projectiles;
    this.state.gameSpeed = nextState.gameSpeed;
    this.state.entityCounter = nextState.entityCounter;
    this.state.skillCooldowns = nextState.skillCooldowns;
    this.state.pendingOrbitalStrikes = nextState.pendingOrbitalStrikes ?? [];
    this.state.queuedGlobalSkills = nextState.queuedGlobalSkills ?? [];
    this.state.activeSkillFields = nextState.activeSkillFields ?? [];
    this.state.pendingWaveReward = nextState.pendingWaveReward;
    this.state.noPathSpawnCount = nextState.noPathSpawnCount;
    this.clock.restore(snapshot.clockTime);
    this.random = nextRandom;
    this.waveSystem = nextWaveSystem;
    this.currentWaveIndex = snapshot.currentWaveIndex;
    this.currentWaveConfig = snapshot.currentWaveConfig
      ? {
          ...snapshot.currentWaveConfig,
          groups: snapshot.currentWaveConfig.groups.map((group) => ({ ...group })),
        }
      : null;
    this.towersBuiltSession = snapshot.towersBuiltSession;
    this.livesLostThisGame = snapshot.livesLostThisGame;
    this.wave1CompleteTime = snapshot.wave1CompleteTime;
  }

  /**
   * 确定性浏览器测试钩子：启动特定配置的波次，无需回放前面的波次。
   * 实际游戏使用 dispatch(start_wave)。
   */
  startWaveForTest(wave: number): void {
    if (!Number.isInteger(wave) || wave < 1 || !this.mapId || !this.waveSystem) return;
    const resolved = this.repository.getMap(this.mapId);
    if (!resolved || (!resolved.map.isEndless && wave > resolved.map.waves.length)) return;
    this.currentWaveIndex = wave - 2;
    this.state.waveCountdown = 0;
    this.state.waveInProgress = false;
    this.startNextWave();
  }

  /** 确定性浏览器测试钩子：设置玩家资源状态。 */
  setGoldForTest(gold: number): void {
    this.state.gold = Math.max(0, Math.floor(gold));
  }

  /** 确定性浏览器测试钩子：设置低于上限的生命值（用于技能测试）。 */
  setLivesForTest(lives: number): void {
    this.state.baseHp = Math.max(0, Math.min(this.state.maxHp, Math.floor(lives)));
  }

  /** 确定性浏览器测试钩子：渲染结果界面。 */
  winForTest(): void {
    if (this.state.status === "won") return;
    this.state.status = "won";
    this.emit({ type: "game_won", time: this.clock.time(), payload: { wave: this.state.wave } });
    this.cancelPendingOrbitalStrikes(this.clock.time());
    this.cancelQueuedGlobalSkills(this.clock.time());
  }

  /** 确定性浏览器测试钩子：无需建造位置检查即可建造塔。 */
  buildTowerForTest(towerType: string, x: number, y: number): void {
    const config = this.repository.getTower(towerType);
    if (!config) {
      throw new CommandRejectedError(
        { type: "build_tower", towerType, position: { x, y } },
        `unknown tower type "${towerType}"`,
      );
    }
    if (this.state.gold < config.cost) {
      throw new CommandRejectedError(
        { type: "build_tower", towerType, position: { x, y } },
        "insufficient gold",
      );
    }
    const resolved = this.repository.getMap(this.mapId as string);
    if (!resolved)
      throw new CommandRejectedError(
        { type: "build_tower", towerType, position: { x, y } },
        "game not running",
      );
    const snapped = snapToGridCentre(x, y, resolved.map.gridSize);
    this.commitTowerBuild(config, snapped, resolved);
  }

  /** 确定性浏览器测试钩子：无需通过命令派发即可设置游戏速度。 */
  setGameSpeedForTest(speed: 1 | 2 | 4): void {
    const allowed = new Set([1, 2, 4]);
    if (!allowed.has(speed))
      throw new CommandRejectedError({ type: "set_game_speed", speed }, `speed must be 1, 2, or 4`);
    this.state.gameSpeed = speed;
    this.emit({ type: "game_speed_changed", time: this.clock.time(), payload: { speed } });
  }

  /** 获取当前运行中激活的难度配置。 */
  getBalance(): BalanceConfig {
    return this.balance;
  }

  /** 当前会话中累计建造的塔数。 */
  getTowersBuiltSession(): number {
    return this.towersBuiltSession;
  }

  /** 本会话中到达基地的敌人累计损失的生命值。 */
  getLivesLostThisGame(): number {
    return this.livesLostThisGame;
  }

  /** 第 1 波完成时的模拟时间（秒）；若尚未完成则为 0。 */
  getWave1CompleteTime(): number {
    return this.wave1CompleteTime;
  }

  /** 当前经过的模拟时间（秒）。 */
  getElapsedTime(): number {
    return this.clock.time();
  }

  getWaveEnemyCounts(): { remaining: number; total: number } {
    const resolved = this.mapId ? this.repository.getMap(this.mapId) : null;
    if (!resolved) return { remaining: 0, total: 0 };

    if (this.state.waveInProgress && this.currentWaveConfig && this.waveSystem) {
      const planned = this.waveSystem.waveEnemyCounts();
      const alive = this.state.enemies.filter(
        (enemy) =>
          !enemy.debugStationary &&
          enemy.waveIndex === this.currentWaveIndex &&
          enemy.alive &&
          !enemy.markedDead,
      ).length;
      return {
        total: planned.total,
        remaining: Math.max(0, planned.total - planned.successfullySpawned + alive),
      };
    }

    const nextWave = this.getWaveConfigAtIndex(this.currentWaveIndex + 1, resolved);
    if (!nextWave) return { remaining: 0, total: 0 };
    const scaled = scaleWaveGroups(nextWave, this.balance.enemyCountMultiplier ?? 1);
    const total = countWaveEnemies(scaled);
    return { remaining: total, total };
  }

  private getWaveConfigAtIndex(waveIndex: number, resolved: ResolvedMap): WaveConfig | null {
    if (waveIndex < 0) return null;
    if (waveIndex < resolved.map.waves.length) {
      const waveId = resolved.map.waves[waveIndex];
      return resolved.waves[waveId] ?? null;
    }
    if (!resolved.map.isEndless) return null;
    return this._generateEndlessWave(waveIndex + 1);
  }

  /** 获取当前激活的地图 id；若尚未开始任何游戏则返回 null。 */
  getCurrentMapId(): string | null {
    return this.mapId;
  }

  /** 订阅指定事件类型；返回取消订阅函数。 */
  on(type: GameEvent["type"], listener: EventListener): () => void {
    return this.eventBus.subscribe(type, listener);
  }

  /**
   * Begin a run on `mapId`. Resets all mutable state deterministically from
   * the map config (start gold/lives, entityCounter = 0).
   *
   * Difficulty is applied via balanceConfig: starting gold/lives are scaled,
   * and the difficulty key is stored so UI can read it back.
   */
  start(mapId: string): void {
    const resolved = this.repository.getMap(mapId);
    if (!resolved) {
      throw new CommandRejectedError({ type: "start_game" }, `unknown map "${mapId}"`);
    }
    this.mapId = mapId;
    for (const enemyType of Object.keys(this.debugEnemyConfigs))
      delete this.debugEnemyConfigs[enemyType];
    this.currentWaveIndex = -1;
    this.currentWaveConfig = null;
    this.towersBuiltSession = 0;
    this.livesLostThisGame = 0;
    this.wave1CompleteTime = 0;
    if (!this.replayInternalRestart) {
      this.replayHeader = this.createReplayHeader(mapId);
      this.replayCommands = [];
      this.replayOrder = 0;
      this.replayEnabled = !this.replayPlayback;
      this.replayRecordingStarted = true;
      if (!this.replayPlayback)
        this.replayCommands.push({ kind: "start", order: this.replayOrder++ });
    }
    this.waveSystem = new WaveSystem(this.waveSystemDeps(resolved));
    this.state.status = "running";

    // Apply difficulty-based starting adjustments.
    const sgMult = this.balance.startingGoldMultiplier ?? 1.0;
    const slMult = this.balance.startingLivesMultiplier ?? 1.0;
    this.state.gold = Math.round(resolved.map.startGold * sgMult);
    this.state.baseHp = Math.round(resolved.map.startLives * slMult);
    this.state.maxHp = Math.round(resolved.map.startLives * slMult);
    this.state.wave = 0;
    this.state.totalWaves = resolved.map.waves.length;
    this.state.isEndless = resolved.map.isEndless === true;
    this.state.waveInProgress = false;
    this.state.towers = [];
    this.state.enemies = [];
    this.state.projectiles = [];
    this.state.entityCounter = 0;
    this.state.skillCooldowns = [];
    this.state.pendingOrbitalStrikes = [];
    this.state.queuedGlobalSkills = [];
    this.state.activeSkillFields = [];
    this.state.pendingWaveReward = undefined;
    this.clock.reset();
    this.emit({ type: "game_started", time: 0, payload: { mapId, difficulty: this.difficulty } });
  }

  /** 应用单个命令；状态无效时抛出 `CommandRejectedError`。 */
  dispatch(command: GameCommand): void {
    const recordingRestart =
      this.replayEnabled && command.type === "restart_game" && this.replayRecordingStarted;
    if (recordingRestart) this.replayInternalRestart = true;
    try {
      this.dispatchCommand(command);
      this.recordReplayCommand(command, true);
    } catch (error) {
      this.recordReplayCommand(command, false);
      throw error;
    } finally {
      if (recordingRestart) this.replayInternalRestart = false;
    }
  }

  private dispatchCommand(command: GameCommand): void {
    switch (command.type) {
      case "start_game":
        throw new CommandRejectedError(command, "use engine.start(mapId)");
      case "build_tower":
        this.buildTower(command);
        break;
      case "upgrade_tower":
        this.upgradeTower(command);
        break;
      case "sell_tower":
        this.sellTower(command);
        break;
      case "start_wave":
        this.startNextWave();
        break;
      case "pause_game":
        this.pause();
        break;
      case "resume_game":
        this.resume();
        break;
      case "set_game_speed":
        this.setSpeed(command.speed);
        break;
      case "use_skill":
        this.useSkill(command);
        break;
      case "restart_game":
        this.restart();
        break;
      case "build_enemy_debug":
        this.buildEnemyDebug(command);
        break;
      default:
        throw new CommandRejectedError(command as never, `unknown command`);
    }
  }

  /**
   * 使用固定 `stepSeconds` 步长将模拟推进到至少 `targetTime` 秒。
   * 以确定性顺序发出所有波次/战斗/移动事件。这是推进时间的唯一方式——
   * 交互游玩和无头批量模拟都依赖此调用。
   */
  advanceTo(targetTime: number): void {
    if (
      this.state.status !== "running" ||
      !Number.isFinite(targetTime) ||
      targetTime < this.clock.time()
    ) {
      return;
    }
    const step = this.stepSeconds;
    let simTime = this.clock.time();
    const simTimeAtCallStart = simTime;
    const resolved = this.repository.getMap(this.mapId as string);
    if (!resolved) return;
    const activeResolved = this.getActiveResolvedMap(resolved);

    // Accumulate whole steps, then a final partial step, so the clock lands
    // exactly on targetTime without overshooting.
    while (simTime < targetTime) {
      const prevTime = simTime; // Capture time BEFORE ticking
      const dt = Math.min(step, targetTime - simTime);
      this.clock.tick(dt);
      this.step(activeResolved, dt, prevTime);
      simTime = this.clock.time();
      if (this.state.status !== "running") break;
    }
    if (this.replayEnabled && this.replayRecordingStarted) {
      this.recordReplayAdvance({
        kind: "advance",
        order: this.replayOrder,
        targetTime,
        deltaSeconds: this.clock.time() - simTimeAtCallStart,
      });
    }
  }

  private step(resolved: ResolvedMap, dt: number, prevTime: number): void {
    if (this.state.status !== "running" || !this.waveSystem) return;
    const pendingWaveReward = this.state.pendingWaveReward;
    this.state.pendingWaveReward = undefined;
    const pendingFinalWave = pendingWaveReward?.isFinal === true;

    // 1. Status / Timer: expire effects and advance active field lifetimes.
    tickStatusEffects(this.state, dt);
    this.updateActiveSkillFields(dt, true);

    // 2. Movement uses controls synchronized at the start of this Tick.
    const moveResults = this.state.enemies.map((enemy) =>
      moveEnemy(enemy, dt, resolved, resolved.enemies[enemy.type]),
    );
    this.state.enemies = this.state.enemies.filter((_, i) => {
      if (moveResults[i]?.reachedBase) {
        const config = resolved.enemies[this.state.enemies[i].type];
        const livesDamage = config?.livesDamage ?? 1;
        const goldReward = calculateMonsterGoldReward(
          config?.goldReward ?? 0,
          this.balance.goldRewardMultiplier ?? 1,
        );
        this.state.baseHp = Math.max(0, this.state.baseHp - livesDamage);
        this.state.gold += goldReward;
        this.livesLostThisGame += livesDamage;
        this.emit({
          type: "player_damaged",
          time: prevTime,
          payload: { livesRemaining: this.state.baseHp, livesLost: livesDamage },
        });
        if (this.state.baseHp <= 0 && this.state.status === "running") {
          this.state.status = "lost";
          this.emit({ type: "game_lost", time: prevTime, payload: { wave: this.state.wave } });
          this.cancelPendingOrbitalStrikes(prevTime);
          this.cancelQueuedGlobalSkills(prevTime);
        }
        return false;
      }
      return true;
    });
    this.updateActiveSkillFields(0, false);

    // 2. Combat runs before wave spawn. New enemies therefore cannot move or attack on their spawn tick.
    if (this.state.status === "running") {
      this.startQueuedGlobalSkill();
      this.updateActiveSkillFields(0, false);

      // Handle wave countdown
      if (this.state.waveCountdown > 0) {
        this.state.waveCountdown -= dt;
        if (this.state.waveCountdown <= 0) {
          this.state.waveCountdown = 0;
          if (this.mode !== "debug") {
            this.startNextWave();
          }
        }
      } else {
        const waveResult = this.waveSystem.advance(this.state, prevTime, dt);
        for (const spawn of waveResult.spawns) {
          const enemy = {
            id: `enemy-${this.state.entityCounter}`,
            type: spawn.type,
            hp: spawn.hp,
            maxHp: spawn.maxHp,
            attack: spawn.attack,
            magic: spawn.magic,
            defense: spawn.defense,
            magicResistance: spawn.magicResistance,
            speed: spawn.speed,
            waveIndex: spawn.waveIndex,
            position: spawn.position,
            waypoints: spawn.waypoints,
            currentWaypointIndex: 0,
            segmentProgress: 0,
            alive: true,
            markedDead: false,
            isBoss: resolved.enemies[spawn.type]?.isBoss,
            statusEffects: [],
            skillTimers: spawn.skillTimers,
            regenTimer: 0,
          } as unknown as EnemyState;
          this.state.enemies.push(enemy);
          this.state.entityCounter += 1;
          const spawnCfg = resolved.enemies[enemy.type];
          this.emit({
            type: spawnCfg?.isBoss ? "boss_spawned" : "enemy_spawned",
            time: prevTime,
            payload: { enemyId: enemy.id, enemyType: enemy.type, wave: this.state.wave },
          });
        }

        if (waveResult.waveCompleted) {
          this.completeCurrentWave();
        }
        if (waveResult.spawnFailedCount > 0) {
          this.emit({
            type: "spawn_failed",
            time: prevTime,
            payload: { count: waveResult.spawnFailedCount, wave: this.state.wave },
          });
        }
      }
    }

    // 新 Spawn 的敌人在本 Tick 移动阶段之后出现；此处同步使其从下一次移动开始冻结。
    if (this.state.status === "running") this.updateActiveSkillFields(0, false);

    // 3. Combat (tower attacks, projectile movement, damage, deaths, gold)
    if (this.state.status === "running") {
      this.advanceOrbitalStrikes(dt);
      const landedStrikes = (this.state.pendingOrbitalStrikes ?? []).filter(
        (strike) => strike.remainingSeconds <= 0,
      );
      const combat = runCombatTick(this.state, dt, {
        ...this.combatDeps(resolved),
        skipStatusTick: true,
        orbitalStrikes: landedStrikes.map((strike) => ({
          ...strike,
          damage: strike.damage ?? 300,
        })),
      });
      for (const event of combat.events) {
        this.emit({ ...event, time: prevTime });
      }
      for (const ev of combat.damageEvents) {
        this.emit({
          type: "enemy_damaged",
          time: prevTime,
          payload: {
            enemyId: ev.enemyId,
            damage: ev.damage,
            damageType: ev.damageType,
            isCritical: ev.isCritical,
            hpRemaining: ev.hpRemaining,
            sourceId: ev.sourceId,
            sourceKind: ev.sourceKind,
          },
        });
      }
      for (const ev of combat.killEvents) {
        this.emit({
          type: "enemy_killed",
          time: prevTime,
          payload: { enemyId: ev.enemyId, enemyType: ev.enemyType, goldReward: ev.goldReward },
        });
      }
      for (const strikeEvent of combat.landedOrbitalStrikes) {
        const strike = landedStrikes.find((item) => item.id === strikeEvent.id);
        this.emit({
          type: "global_skill_impact",
          time: prevTime,
          payload: {
            skillId: "area_damage",
            impactId: strikeEvent.id,
            position: strikeEvent.position,
            damage: strike?.damage ?? 300,
          },
        });
        const pending = this.state.pendingOrbitalStrikes?.find(
          (entry) => entry.id === strikeEvent.id,
        );
        if (pending) this.removeOrbitalStrike(pending);
      }
      // Drop killed enemies from the live array.
      this.state.enemies = this.state.enemies.filter((e) => e.alive);
      // Tick attack speed boosts (must be done even with skipStatusTick=true)
      tickAttackSpeedBoosts(this.state, dt);
    }

    // Combat 的死亡与击杀奖励先结算，之后发放前一 Tick 完成波次的奖励。
    if (pendingWaveReward) {
      this.state.gold += pendingWaveReward.gold;
      this.emit({
        type: "wave_reward_granted",
        time: this.clock.time(),
        payload: { wave: pendingWaveReward.wave, rewardGold: pendingWaveReward.gold },
      });
      if (pendingFinalWave && this.state.status === "running") {
        this.state.status = "won";
        this.emit({
          type: "game_won",
          time: this.clock.time(),
          payload: { wave: pendingWaveReward.wave },
        });
        this.cancelPendingOrbitalStrikes(this.clock.time());
        this.cancelQueuedGlobalSkills(this.clock.time());
      }
    }
  }

  private buildEnemyDebug(command: Extract<GameCommand, { type: "build_enemy_debug" }>): void {
    if (this.mode !== "debug") {
      throw new CommandRejectedError(command, "debug mode required");
    }
    if ((this.state.status !== "running" && this.state.status !== "paused") || !this.mapId) {
      throw new CommandRejectedError(command, "game not running");
    }
    const resolved = this.repository.getMap(this.mapId);
    const config = this.repository.getEnemy(command.enemyType);
    if (!resolved || !config) {
      throw new CommandRejectedError(command, `unknown enemy type "${command.enemyType}"`);
    }
    const { width, height, gridSize } = resolved.map;
    const position = snapToGridCentre(command.position.x, command.position.y, gridSize);
    if (position.x < 0 || position.x > width || position.y < 0 || position.y > height) {
      throw new CommandRejectedError(command, "position is outside map bounds");
    }
    this.debugEnemyConfigs[config.id] = config;
    const id = `enemy-${this.state.entityCounter}`;
    this.state.entityCounter += 1;
    const skillTimers = (config.skillIds ?? []).reduce<Record<string, number>>(
      (timers, skillId) => {
        const skill = this.content.skills[skillId];
        const condition = skill?.releaseCondition;
        if (skill?.type === "MONSTER" && condition?.kind === "interval") {
          timers[skillId] = condition.initialDelay ?? condition.seconds;
        }
        return timers;
      },
      {},
    );
    const enemy: EnemyState = {
      id,
      type: config.id,
      hp: config.stats.maxHp,
      maxHp: config.stats.maxHp,
      attack: config.stats.attack,
      magic: config.stats.magic,
      defense: config.stats.defense,
      magicResistance: config.stats.magicResistance,
      position: { ...position },
      currentWaypointIndex: 0,
      segmentProgress: 0,
      waypoints: [{ ...position }],
      alive: true,
      markedDead: false,
      debugStationary: true,
      isBoss: config.isBoss,
      statusEffects: [],
      skillTimers,
      regenTimer: 0,
    };
    this.state.enemies.push(enemy);
    this.emit({
      type: config.isBoss ? "boss_spawned" : "enemy_spawned",
      time: this.clock.time(),
      payload: { enemyId: id, enemyType: config.id, debug: true },
    });
  }

  private buildTower(command: Extract<GameCommand, { type: "build_tower" }>): void {
    if ((this.state.status !== "running" && this.state.status !== "paused") || !this.mapId) {
      throw new CommandRejectedError(command, "game not running");
    }
    const config = this.repository.getTower(command.towerType);
    if (!config) {
      throw new CommandRejectedError(command, `unknown tower type "${command.towerType}"`);
    }
    const resolved = this.repository.getMap(this.mapId);
    if (!resolved) throw new CommandRejectedError(command, "game not running");
    // Snap the click position to the nearest grid cell centre.
    const snapped = snapToGridCentre(command.position.x, command.position.y, resolved.map.gridSize);
    if (!this.isBuildable(snapped)) {
      throw new CommandRejectedError(command, "position is not buildable");
    }
    if (this.state.gold < config.cost && this.mode !== "debug") {
      throw new CommandRejectedError(command, "insufficient gold");
    }
    this.commitTowerBuild(config, snapped, resolved);
  }

  private commitTowerBuild(config: TowerConfig, position: Point, resolved: ResolvedMap): void {
    const candidateTowers = [...this.state.towers.map((tower) => tower.position), position];
    const replans = this.state.enemies
      .filter((enemy) => enemy.alive && !enemy.debugStationary)
      .map((enemy) => ({ enemy, path: replanEnemyPath(enemy, candidateTowers, resolved) }));
    if (replans.some(({ path }) => path === null)) {
      throw new CommandRejectedError(
        { type: "build_tower", towerType: config.id, position },
        "would block a live enemy path",
      );
    }

    const towerId = `tower-${this.state.entityCounter}`;
    this.state.entityCounter += 1;
    if (this.mode !== "debug") this.state.gold -= config.cost;
    this.towersBuiltSession += 1;
    this.state.towers.push({
      id: towerId,
      type: config.id,
      position: { ...position },
      level: 1,
      cooldown: 1 / config.attackSpeed,
      buildCost: config.cost,
      upgradeCosts: [],
      skillReleaseProgress: Object.fromEntries(
        (config.skillIds ?? []).map((skillId) => [skillId, 0]),
      ),
      attackSpeedBoosts: [],
      pendingAttackSkillId: undefined,
      laserTargetId: undefined,
      laserPulseCount: 0,
    } as unknown as TowerState);
    for (const { enemy, path } of replans) {
      enemy.waypoints = path!;
      enemy.currentWaypointIndex = 0;
      enemy.segmentProgress = 0;
    }
    this.emit({
      type: "tower_built",
      time: this.clock.time(),
      payload: { towerId, towerType: config.id, position, goldSpent: config.cost },
    });
  }
  private upgradeTower(command: Extract<GameCommand, { type: "upgrade_tower" }>): void {
    if (this.state.status !== "running" && this.state.status !== "paused") {
      throw new CommandRejectedError(command, "game not running");
    }
    const tower = this.state.towers.find((t) => t.id === command.towerId);
    if (!tower) throw new CommandRejectedError(command, `unknown tower "${command.towerId}"`);
    const config = this.repository.getTower(tower.type);
    if (!config || tower.level >= config.maxLevel) {
      throw new CommandRejectedError(command, "tower already at max level");
    }
    const upgrade = config.upgrades[tower.level];
    const cost = upgrade?.upgradeCost ?? 0;
    if (this.state.gold < cost && this.mode !== "debug") {
      throw new CommandRejectedError(command, "insufficient gold to upgrade");
    }
    if (this.mode !== "debug") this.state.gold -= cost;
    tower.level += 1;
    tower.upgradeCosts.push(cost);
    this.emit({
      type: "tower_upgraded",
      time: this.clock.time(),
      payload: { towerId: tower.id, level: tower.level, goldSpent: cost },
    });
  }

  private sellTower(command: Extract<GameCommand, { type: "sell_tower" }>): void {
    if (this.state.status !== "running" && this.state.status !== "paused") {
      throw new CommandRejectedError(command, "game not running");
    }
    const index = this.state.towers.findIndex((t) => t.id === command.towerId);
    if (index < 0) throw new CommandRejectedError(command, `unknown tower "${command.towerId}"`);
    const tower = this.state.towers[index];
    const config = this.repository.getTower(tower.type);
    if (!config) throw new CommandRejectedError(command, "unknown tower config");
    const refund = Math.round(
      tower.buildCost + 0.7 * tower.upgradeCosts.reduce((a, b) => a + b, 0),
    );
    this.state.gold += refund;
    this.state.towers.splice(index, 1);
    this.emit({
      type: "tower_sold",
      time: this.clock.time(),
      payload: { towerId: tower.id, goldRefunded: refund },
    });
  }

  private startNextWave(): void {
    if (this.state.waveInProgress || this.state.waveCountdown > 0) {
      return;
    }
    if (!this.mapId) return;
    const resolved = this.repository.getMap(this.mapId);
    if (!resolved) return;
    this.currentWaveIndex += 1;
    if (this.mode !== "debug" && !this.mapId) return;

    const wave = this.getWaveConfigAtIndex(this.currentWaveIndex, resolved);

    if (!wave) {
      // 无可用波次，结束游戏（不应在无尽模式下触发）
      return;
    }

    this.currentWaveConfig = wave;

    if (!this.waveSystem) {
      this.waveSystem = new WaveSystem(this.waveSystemDeps(resolved));
    } else {
      this.waveSystem.updateDeps(this.waveSystemDeps(resolved));
    }
    this.waveSystem.startWave(wave, this.state, this.clock.time(), this.currentWaveIndex);
    this.state.wave = wave.wave;
    this.emit({ type: "wave_started", time: this.clock.time(), payload: { wave: wave.wave } });
  }

  private _generateEndlessWave(waveNumber: number, seed = this.initialSeed): WaveConfig | null {
    if (!this.mapId) return null;
    const config = this.content.endlessWaves?.[this.mapId];
    if (!config || waveNumber < config.startWave) return null;
    return generateEndlessWave(
      config,
      this.content.enemies,
      this.content.bosses,
      seed,
      this.mapId,
      waveNumber,
    );
  }

  private contentFingerprint(mapId: string | null = this.mapId): string {
    const currentMapId = mapId ?? "";
    const difficultyKey = normalizeDifficulty(this.difficulty);
    const mapBalance = this.content.difficultyBalances?.[difficultyKey] ?? this.content.balance;
    const combatTuning = this.content.combatTuning ?? {
      id: "combat-compatibility-default",
      splashDamageMultiplier: 0.5,
      chainDamageMultiplier: 0.5,
      towerAutoSkill: { damageMultiplier: 2 },
    };
    const content = JSON.stringify({
      map: this.content.maps[currentMapId] ?? null,
      waves: this.content.waves,
      towers: this.content.towers,
      enemies: this.content.enemies,
      bosses: this.content.bosses,
      skills: this.content.skills,
      endless: this.content.endlessWaves?.[currentMapId] ?? null,
      balance: mapBalance,
      combatTuning,
    });
    let hashA = 0x811c9dc5;
    let hashB = 0x9e3779b9;
    for (let index = 0; index < content.length; index += 1) {
      const code = content.charCodeAt(index);
      hashA = Math.imul(hashA ^ code, 0x01000193);
      hashB = Math.imul(hashB ^ code, 0x85ebca6b);
    }
    return `runtime-v1-${(hashA >>> 0).toString(16).padStart(8, "0")}${(hashB >>> 0).toString(16).padStart(8, "0")}`;
  }

  private waveSystemDeps(resolved: ResolvedMap, random = this.random): WaveSystemDeps {
    return {
      resolvedMap: resolved,
      random,
      balance: this.balance,
      skills: this.content.skills,
    };
  }

  private completeCurrentWave(): void {
    if (!this.mapId || !this.currentWaveConfig) return;
    const resolved = this.repository.getMap(this.mapId);
    if (!resolved) return;
    const wave = this.currentWaveConfig;

    this.state.pendingWaveReward = {
      wave: wave.wave,
      gold: wave.rewardGold ?? 0,
      isFinal:
        wave.isFinal === true &&
        this.currentWaveIndex >= resolved.map.waves.length - 1 &&
        !resolved.map.isEndless,
    };
    this.state.baseHp = Math.min(this.state.maxHp, this.state.baseHp + (wave.rewardLives ?? 0));
    this.state.waveInProgress = false;
    this.currentWaveConfig = null;
    this.state.waveCountdown = 5;

    if (wave.wave === 1 && this.wave1CompleteTime === 0) {
      this.wave1CompleteTime = this.clock.time();
    }

    this.emit({
      type: "wave_completed",
      time: this.clock.time(),
      payload: { wave: wave.wave, rewardGold: wave.rewardGold ?? 0 },
    });
  }

  private pause(): void {
    if (this.state.status !== "running") return;
    this.state.status = "paused";
    this.emit({ type: "game_paused", time: this.clock.time() });
  }

  private resume(): void {
    if (this.state.status !== "paused") return;
    this.state.status = "running";
    this.emit({ type: "game_resumed", time: this.clock.time() });
  }

  /**
   * Restart the game: reset to initial state with the same seed and map.
   * Per 《胜利与重启规则.md》§6: clears all entities, timers, projectiles,
   * status effects, and wave progress; resets random sequence.
   */
  private restart(): void {
    const mapId = this.mapId;
    if (!mapId) return;
    // Reset random sequence to initial position.
    this.random = new SeededRandom(this.initialSeed);
    this.start(mapId);
  }

  private setSpeed(speed: number): void {
    const allowed = new Set([1, 2, 4]);
    if (!allowed.has(speed)) {
      throw new CommandRejectedError({ type: "set_game_speed", speed }, `speed must be 1, 2, or 4`);
    }
    this.state.gameSpeed = speed;
    this.emit({ type: "game_speed_changed", time: this.clock.time(), payload: { speed } });
  }

  private useSkill(command: Extract<GameCommand, { type: "use_skill" }>): void {
    if ((this.state.status !== "running" && this.state.status !== "paused") || !this.mapId) {
      throw new CommandRejectedError(command, "game not running");
    }
    const resolved = this.repository.getMap(this.mapId);
    if (!resolved) throw new CommandRejectedError(command, "game not running");
    const skillConfig = this.repository.getSkill(command.skillId);
    if (!skillConfig) {
      throw new CommandRejectedError(command, `unknown skill "${command.skillId}"`);
    }
    if (skillConfig.type !== "GLOBAL") {
      throw new CommandRejectedError(command, `skill "${command.skillId}" is not a GLOBAL skill`);
    }
    // Position defaults to the map centre for area skills.
    const position: Point = command.position ?? {
      x: resolved.map.width / 2,
      y: resolved.map.height / 2,
    };
    if (this.state.gold < skillConfig.cost && this.mode !== "debug") {
      throw new CommandRejectedError(command, "insufficient gold");
    }
    const cooldown = this.state.skillCooldowns.find((entry) => entry.id === command.skillId);
    if (this.mode !== "debug" && cooldown && this.clock.time() < cooldown.cooldownEnd) {
      throw new CommandRejectedError(command, `skill "${command.skillId}" is on cooldown`);
    }

    const pendingStrikes = (this.state.pendingOrbitalStrikes ??= []);
    const queuedSkills = (this.state.queuedGlobalSkills ??= []);
    const strike: PendingOrbitalStrike | null =
      command.skillId === "area_damage" && this.state.status === "running"
        ? {
            id: `orbital-strike-${this.state.entityCounter++}`,
            position: { ...position },
            damage: this.state.wave * 30 + 500,
            remainingSeconds: ORBITAL_STRIKE_FLIGHT_SECONDS,
          }
        : null;
    const queuedSkill: QueuedGlobalSkill | null =
      this.state.status === "paused"
        ? {
            id: `global-skill-${this.state.entityCounter++}`,
            skillId: command.skillId,
            position: { ...position },
            ...(command.skillId === "area_damage" ? { damage: this.state.wave * 30 + 500 } : {}),
          }
        : null;

    if (this.mode !== "debug") {
      const cooldownEnd = this.clock.time() + skillConfig.cooldown;
      const existingIndex = this.state.skillCooldowns.findIndex((s) => s.id === command.skillId);
      if (existingIndex >= 0) {
        this.state.skillCooldowns[existingIndex] = { id: command.skillId, cooldownEnd };
      } else {
        this.state.skillCooldowns.push({ id: command.skillId, cooldownEnd });
      }
      this.state.gold -= skillConfig.cost;
    }

    if (queuedSkill) {
      queuedSkills.push(queuedSkill);
      return;
    }

    if (this.state.status === "paused") return;

    if (strike) {
      pendingStrikes.push(strike);
      this.emit({
        type: "skill_used",
        time: this.clock.time(),
        payload: {
          skillId: command.skillId,
          position,
          impactId: strike.id,
          flightSeconds: ORBITAL_STRIKE_FLIGHT_SECONDS,
          damage: strike.damage,
        },
      });
      return;
    }

    if (command.skillId === "slow_all") {
      const field = this.createGlobalSkillField(
        command.skillId,
        position,
        skillConfig.radius,
        skillConfig.control?.duration,
      );
      if (field) {
        this.emit({
          type: "skill_used",
          time: this.clock.time(),
          payload: { skillId: command.skillId, position },
        });
        return;
      }
    }

    const skillResult = applySkillDamage(
      this.state,
      skillConfig,
      position,
      this.combatDeps(resolved),
    );
    this.emitSkillDamageEvents(skillResult.damageEvents, this.clock.time());
    for (const event of skillResult.events) {
      this.emit({ ...event, time: this.clock.time() });
    }
    for (const event of skillResult.killEvents) {
      this.emit({
        type: "enemy_killed",
        time: this.clock.time(),
        payload: {
          enemyId: event.enemyId,
          enemyType: event.enemyType,
          goldReward: event.goldReward,
          isBoss: event.isBoss,
        },
      });
    }
    this.state.enemies = this.state.enemies.filter((enemy) => enemy.alive);
    this.emit({
      type: "skill_used",
      time: this.clock.time(),
      payload: { skillId: command.skillId, position },
    });
  }

  private createGlobalSkillField(
    skillId: string,
    position: Point,
    radius: number | undefined,
    durationSeconds: number | undefined,
    id = `skill-field-${this.state.entityCounter++}`,
  ): ActiveSkillField | null {
    if (
      skillId !== "slow_all" ||
      typeof radius !== "number" ||
      !Number.isFinite(radius) ||
      radius <= 0 ||
      typeof durationSeconds !== "number" ||
      !Number.isFinite(durationSeconds) ||
      durationSeconds <= 0
    ) {
      return null;
    }

    const field: ActiveSkillField = {
      id,
      skillId,
      position: { ...position },
      radius,
      durationSeconds,
      remainingSeconds: durationSeconds,
      affectedEnemyIds: [],
    };
    (this.state.activeSkillFields ??= []).push(field);
    this.syncFieldMembership(field);
    return field;
  }

  /** 推进风暴场寿命并按各自来源同步冻结状态。 */
  private updateActiveSkillFields(dt: number, advanceTime: boolean): void {
    const fields = (this.state.activeSkillFields ??= []);
    for (let index = fields.length - 1; index >= 0; index--) {
      const field = fields[index];
      if (advanceTime) field.remainingSeconds = Math.max(0, field.remainingSeconds - dt);
      this.syncFieldMembership(field);
      if (field.remainingSeconds <= 0) {
        this.removeFieldFreezes(field);
        fields.splice(index, 1);
      }
    }
  }

  private syncFieldMembership(field: ActiveSkillField): void {
    const currentIds = new Set<string>();
    for (const enemy of this.state.enemies) {
      if (!enemy.alive || enemy.markedDead) continue;
      const inside =
        Math.hypot(enemy.position.x - field.position.x, enemy.position.y - field.position.y) <=
        field.radius;
      if (!inside) continue;
      currentIds.add(enemy.id);
      const fieldFreeze = enemy.statusEffects.find(
        (effect) => effect.controlType === "stun" && effect.source === field.id,
      );
      if (fieldFreeze) {
        fieldFreeze.remaining = field.remainingSeconds;
      } else {
        enemy.statusEffects.push({
          key: `stun:${field.id}`,
          remaining: field.remainingSeconds,
          modifiers: [],
          controlType: "stun",
          source: field.id,
        });
      }
    }

    const previousIds = new Set(field.affectedEnemyIds);
    for (const enemy of this.state.enemies) {
      if (currentIds.has(enemy.id) || !previousIds.has(enemy.id)) continue;
      this.removeFieldFreeze(enemy, field.id);
    }
    field.affectedEnemyIds = [...currentIds];
  }

  private removeFieldFreezes(field: ActiveSkillField): void {
    for (const enemyId of field.affectedEnemyIds) {
      const enemy = this.state.enemies.find((candidate) => candidate.id === enemyId);
      if (enemy) this.removeFieldFreeze(enemy, field.id);
    }
    field.affectedEnemyIds = [];
  }

  private removeFieldFreeze(enemy: EnemyState, fieldId: string): void {
    enemy.statusEffects = enemy.statusEffects.filter(
      (effect) => !(effect.controlType === "stun" && effect.source === fieldId),
    );
  }

  /** 恢复后每个 Tick 最多发射一个暂停期间排队的全局技能。 */
  private startQueuedGlobalSkill(): void {
    const queue = this.state.queuedGlobalSkills;
    const queued = queue?.shift();
    if (!queued) return;
    if (queued.skillId === "area_damage") {
      const strike: PendingOrbitalStrike = {
        id: queued.id,
        position: queued.position,
        damage: queued.damage ?? 300,
        remainingSeconds: ORBITAL_STRIKE_FLIGHT_SECONDS,
      };
      (this.state.pendingOrbitalStrikes ??= []).push(strike);
      this.emit({
        type: "skill_used",
        time: this.clock.time(),
        payload: {
          skillId: queued.skillId,
          position: queued.position,
          impactId: strike.id,
          flightSeconds: ORBITAL_STRIKE_FLIGHT_SECONDS,
          damage: strike.damage,
        },
      });
      return;
    }

    const resolved = this.mapId ? this.repository.getMap(this.mapId) : null;
    const skill = this.repository.getSkill(queued.skillId);
    if (!skill || !resolved) return;
    if (queued.skillId === "slow_all") {
      const field = this.createGlobalSkillField(
        queued.skillId,
        queued.position,
        skill.radius,
        skill.control?.duration,
        queued.id,
      );
      if (!field) return;
      this.emit({
        type: "skill_used",
        time: this.clock.time(),
        payload: { skillId: queued.skillId, position: queued.position, fieldId: field.id },
      });
      return;
    }
    this.state.enemies = this.state.enemies.filter((enemy) => enemy.alive);
    this.emit({
      type: "skill_used",
      time: this.clock.time(),
      payload: { skillId: queued.skillId, position: queued.position },
    });
  }

  /** 飞行计时在 Game Core 内推进，到期后先标记落地，再按 Tick 阶段顺序结算。 */
  private advanceOrbitalStrikes(dt: number): void {
    if (this.state.status !== "running") return;
    for (const strike of this.state.pendingOrbitalStrikes ?? []) {
      strike.remainingSeconds = Math.max(0, strike.remainingSeconds - dt);
    }
  }

  private emitSkillDamageEvents(
    damageEvents: Array<{
      enemyId: string;
      damage: number;
      damageType: import("../types").DamageType;
      isCritical: boolean;
      hpRemaining: number;
      sourceId?: string;
      sourceKind?: "tower" | "enemy" | "skill" | "environment";
    }>,
    time: number,
  ): void {
    for (const event of damageEvents) {
      this.emit({
        type: "enemy_damaged",
        time,
        payload: {
          enemyId: event.enemyId,
          damage: event.damage,
          damageType: event.damageType,
          isCritical: event.isCritical,
          hpRemaining: event.hpRemaining,
          sourceId: event.sourceId,
          sourceKind: event.sourceKind,
        },
      });
    }
  }

  private cancelOrbitalStrike(strike: PendingOrbitalStrike, time: number): void {
    this.emit({
      type: "global_skill_cancelled",
      time,
      payload: { skillId: "area_damage", impactId: strike.id },
    });
    this.removeOrbitalStrike(strike);
  }

  private cancelPendingOrbitalStrikes(time: number): void {
    for (const strike of this.state.pendingOrbitalStrikes ?? []) {
      this.cancelOrbitalStrike(strike, time);
    }
  }

  private cancelQueuedGlobalSkills(time: number): void {
    const queuedSkills = (this.state.queuedGlobalSkills ??= []);
    const queued = queuedSkills.splice(0);
    for (const skill of queued) {
      this.emit({
        type: "global_skill_cancelled",
        time,
        payload: { skillId: skill.skillId, impactId: skill.id },
      });
    }
  }

  private removeOrbitalStrike(strike: PendingOrbitalStrike): void {
    const strikes = this.state.pendingOrbitalStrikes;
    if (!strikes) return;
    const index = strikes.findIndex((entry) => entry.id === strike.id);
    if (index >= 0) strikes.splice(index, 1);
  }

  /** A position is buildable if it's on a grid cell centre within the map,
   * and no existing tower occupies an adjacent or overlapping cell. */
  private isBuildable(position: Point): boolean {
    const resolved = this.repository.getMap(this.mapId as string);
    if (!resolved) return false;
    const map = resolved.map;
    if (position.x < 0 || position.x > map.width || position.y < 0 || position.y > map.height)
      return false;
    const occupied = occupiedGridCells(
      this.state.towers.map((t) => t.position),
      map.gridSize,
    );
    const { gx, gy } = pixelToGrid(position.x, position.y, map.gridSize);
    return !occupied.has(`${gx},${gy}`);
  }

  private getActiveResolvedMap(resolved: ResolvedMap): ResolvedMap {
    if (Object.keys(this.debugEnemyConfigs).length === 0) return resolved;
    return {
      ...resolved,
      enemies: { ...resolved.enemies, ...this.debugEnemyConfigs },
    };
  }

  private combatDeps(resolved: ResolvedMap): CombatSystemDeps {
    return {
      resolvedMap: this.getActiveResolvedMap(resolved),
      skills: this.content.skills,
      balance: this.balance,
      random: this.random,
      tuning: this.combatTuning,
      baseDistanceField: this.baseDistanceFieldCache.get(
        this.state.towers.map((tower) => tower.position),
        this.getActiveResolvedMap(resolved),
      ),
    };
  }

  private isCompatibleWave(
    wave: WaveConfig,
    resolved: ResolvedMap,
    scaled = false,
    seed = this.initialSeed,
  ): boolean {
    if (wave.mapId !== this.mapId) return false;
    const expected = this.getExpectedWaveConfig(wave.wave, resolved, seed);
    if (!expected || expected.id !== wave.id) return false;
    const normalized = scaled
      ? scaleWaveGroups(expected, this.balance.enemyCountMultiplier ?? 1)
      : expected;
    return JSON.stringify(normalized) === JSON.stringify(wave);
  }

  private getExpectedWaveConfig(
    waveNumber: number,
    resolved: ResolvedMap,
    seed = this.initialSeed,
  ): WaveConfig | null {
    const index = waveNumber - 1;
    if (index < resolved.map.waves.length) {
      const waveId = resolved.map.waves[index];
      return resolved.waves[waveId] ?? null;
    }
    if (!resolved.map.isEndless) return null;
    return this._generateEndlessWave(waveNumber, seed);
  }

  private emit(event: GameEvent): void {
    if (this.replayEffectsMuted && this.isTransientReplayEvent(event.type)) return;
    this.eventBus.emit(event);
  }

  private isTransientReplayEvent(type: GameEvent["type"]): boolean {
    return (
      type === "enemy_damaged" ||
      type === "enemy_killed" ||
      type === "tower_built" ||
      type === "tower_upgraded" ||
      type === "tower_sold" ||
      type === "attack_started" ||
      type === "attack_hit" ||
      type === "critical_hit" ||
      type === "area_damage_impact" ||
      type === "chain_lightning" ||
      type === "lightning_strike" ||
      type === "skill_used" ||
      type === "global_skill_impact" ||
      type === "skill_cast" ||
      type === "unit_killed"
    );
  }
}

function createInitialGameState(): GameState {
  return {
    status: "menu",
    time: 0,
    gold: 0,
    baseHp: 0,
    maxHp: 0,
    wave: 0,
    totalWaves: 0,
    isEndless: false,
    waveInProgress: false,
    waveCountdown: 0,
    towers: [],
    enemies: [],
    projectiles: [],
    gameSpeed: 1,
    entityCounter: 0,
    skillCooldowns: [],
    pendingOrbitalStrikes: [],
    queuedGlobalSkills: [],
    activeSkillFields: [],
    pendingWaveReward: undefined,
    noPathSpawnCount: 0,
  };
}
